/**
 * HitlSession (I2 · takeover + I3 · live-help · Figma 126:316 + 128:51)
 *
 * The live session view. Two-column layout:
 *   Left  — context: learner identity, source lecture, AI conversation
 *           history, code snapshot
 *   Right — live conversation: message stream + composer, annotation
 *           picker (line range + note), "Handback" CTA that navigates
 *           to /hitl/session/:id/handback
 *
 * Data flow:
 *   - GET context before every initial/reconnect room join
 *   - generic canonical WS fanout reconciles messages and annotations
 *   - POST message / annotation is the only write path
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Annotation,
  HITL_ANNOTATION_MAX_LENGTH,
  HITL_MESSAGE_MAX_LENGTH,
  HitlMessage,
  SessionContext,
  createClientMessageId,
  hitlApi,
} from '../../services/hitlApi';
import { wsService } from '../../services/websocket';
import { styles, formatTime } from './HitlStyles';

export function mergeHitlMessages(
  current: HitlMessage[],
  incoming: HitlMessage,
): HitlMessage[] {
  const duplicate = current.some(
    (message) =>
      message.id === incoming.id ||
      (incoming.client_message_id &&
        message.client_message_id === incoming.client_message_id) ||
      (message.sequence === incoming.sequence &&
        message.session_id === incoming.session_id),
  );
  if (duplicate) return current;
  return [...current, incoming].sort((a, b) => a.sequence - b.sequence);
}

export function mergeHitlAnnotations(
  current: Annotation[],
  incoming: Annotation,
): Annotation[] {
  return current.some((annotation) => annotation.id === incoming.id)
    ? current
    : [...current, incoming];
}

export interface PendingInstructorMessage {
  content: string;
  clientMessageId: string;
}

export function resolvePendingInstructorMessage(
  current: PendingInstructorMessage | null,
  content: string,
  createId: () => string = createClientMessageId,
): PendingInstructorMessage {
  const normalized = content.trim();
  return current?.content === normalized
    ? current
    : { content: normalized, clientMessageId: createId() };
}

export async function reconcileInstructorJoin<T>(
  hydrateSnapshot: () => Promise<T | null>,
  isActiveSnapshot: (snapshot: T) => boolean,
  join: () => Promise<{ ok?: boolean; error?: string }>,
): Promise<{ snapshot: T | null; ack: { ok?: boolean; error?: string } | null }> {
  const snapshot = await hydrateSnapshot();
  if (!snapshot || !isActiveSnapshot(snapshot)) return { snapshot, ack: null };
  const ack = await join();
  if (ack.ok) await hydrateSnapshot();
  return { snapshot, ack };
}

interface HitlSessionViewProps {
  id: string;
}

const HitlSessionView: React.FC<HitlSessionViewProps> = ({ id }) => {
  const nav = useNavigate();
  const [ctx, setCtx] = useState<SessionContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  const [presence, setPresence] = useState<'joined' | 'left' | 'disconnected' | 'idle'>('idle');
  const [idePeek, setIdePeek] = useState<{ file_path: string; content: string } | null>(null);
  const [annotationOpen, setAnnotationOpen] = useState(false);
  const [annStart, setAnnStart] = useState<number>(1);
  const [annEnd, setAnnEnd] = useState<number>(1);
  const [annNote, setAnnNote] = useState<string>('');
  const pendingMessageRef = useRef<PendingInstructorMessage | null>(null);
  const confirmedClientIdsRef = useRef(new Set<string>());
  const generationRef = useRef(0);

  // Canonical DB snapshot is loaded before every initial/reconnect room join.
  useEffect(() => {
    const generation = ++generationRef.current;
    let active = true;
    let joining = false;
    let reconcilePromise: ReturnType<typeof hitlApi.getContext> | null = null;
    let pollId: ReturnType<typeof setInterval> | undefined;
    const isCurrent = () =>
      active && generationRef.current === generation;
    setCtx(null);
    setError(null);
    setComposer('');
    setSending(false);
    setIdePeek(null);
    setPresence('idle');
    setAnnotationOpen(false);
    setAnnStart(1);
    setAnnEnd(1);
    setAnnNote('');
    pendingMessageRef.current = null;
    confirmedClientIdsRef.current.clear();

    const reconcile = () => {
      if (!isCurrent()) return Promise.resolve(null);
      if (reconcilePromise) return reconcilePromise;
      reconcilePromise = hitlApi.getContext(id)
        .then((context) => {
          if (!isCurrent()) return context;
          const pending = pendingMessageRef.current;
          if (
            pending &&
            context.messages.some(
              (message) =>
                message.client_message_id === pending.clientMessageId,
            )
          ) {
            confirmedClientIdsRef.current.add(pending.clientMessageId);
            pendingMessageRef.current = null;
            setComposer((current) =>
              current.trim() === pending.content ? '' : current,
            );
            setError(null);
          }
          setCtx(context);
          if (context?.session.status !== 'active' && pollId) {
            clearInterval(pollId);
            pollId = undefined;
          }
          return context;
        })
        .finally(() => {
          reconcilePromise = null;
        });
      return reconcilePromise;
    };

    const hydrateThenJoin = async () => {
      if (!isCurrent() || joining) return;
      joining = true;
      try {
        if (wsService.isConnected()) {
          const { ack } = await reconcileInstructorJoin(
            reconcile,
            (context) => context.session.status === 'active',
            () =>
              isCurrent()
                ? wsService.emitWithAck<{ ok?: boolean; error?: string }>(
                    'hitl:session:join',
                    { session_id: id },
                  )
                : Promise.resolve({ error: 'stale_session' }),
          );
          if (!isCurrent()) return;
          if (ack && !ack.ok) {
            throw new Error('The live session room is unavailable');
          }
        } else {
          await reconcile();
        }
      } catch (err) {
        if (isCurrent()) {
          setError(err instanceof Error ? err.message : 'Failed to load session');
        }
      } finally {
        joining = false;
      }
    };

    const offConnect = wsService.on('connect', () => {
      void hydrateThenJoin();
    });
    const offDisconnect = wsService.on('disconnect', () => {
      if (isCurrent()) setPresence('disconnected');
    });
    const offMsg = wsService.on<HitlMessage>('hitl:session:message', (message) => {
      if (!isCurrent() || message.session_id !== id) return;
      setCtx((prev) => {
        if (!prev) return prev;
        return { ...prev, messages: mergeHitlMessages(prev.messages, message) };
      });
      if (
        message.client_message_id &&
        pendingMessageRef.current?.clientMessageId === message.client_message_id
      ) {
        confirmedClientIdsRef.current.add(message.client_message_id);
        const confirmedContent = pendingMessageRef.current.content;
        pendingMessageRef.current = null;
        setComposer((current) =>
          current.trim() === confirmedContent ? '' : current,
        );
        setError(null);
      }
    });

    const offPresence = wsService.on<{ session_id?: string; state: string }>(
      `hitl:session:${id}:presence`,
      (payload) => {
        if (!isCurrent()) return;
        if (payload.session_id && payload.session_id !== id) return;
        if (payload.state === 'joined') setPresence('joined');
        else if (payload.state === 'left') setPresence('left');
        else if (payload.state === 'disconnected') setPresence('disconnected');
      },
    );

    const offAnno = wsService.on<Annotation>('hitl:session:annotation', (annotation) => {
      if (!isCurrent() || annotation.session_id !== id) return;
      setCtx((prev) => {
        if (!prev) return prev;
        return { ...prev, annotations: mergeHitlAnnotations(prev.annotations, annotation) };
      });
    });

    const offPeek = wsService.on<{ file_path: string; content: string }>(
      `hitl:session:${id}:ide-peek`,
      (payload) => {
        if (isCurrent()) setIdePeek(payload);
      },
    );
    const offResolved = wsService.on<{ session_id?: string }>(
      'hitl:session:resolved',
      (payload) => {
        if (!isCurrent() || payload.session_id !== id) return;
        if (pollId) clearInterval(pollId);
        pollId = undefined;
        setCtx((prev) =>
          prev ? { ...prev, session: { ...prev.session, status: 'ended' } } : prev,
        );
      },
    );

    pollId = setInterval(() => {
      void reconcile().catch((err) => {
        if (isCurrent()) {
          setError(err instanceof Error ? err.message : 'Failed to reconcile session');
        }
      });
    }, 5_000);
    void hydrateThenJoin();

    return () => {
      active = false;
      if (generationRef.current === generation) {
        generationRef.current += 1;
      }
      if (pollId) clearInterval(pollId);
      offConnect?.();
      offDisconnect?.();
      offMsg?.();
      offPresence?.();
      offAnno?.();
      offPeek?.();
      offResolved?.();
      wsService.emit('hitl:session:leave', { session_id: id });
    };
  }, [id]);

  const codeSnapshot = useMemo(() => {
    const snap = ctx?.escalation.code_snapshot;
    if (!snap || typeof snap !== 'object') return null;
    return snap as { file_path?: string; content?: string; terminal_tail?: string };
  }, [ctx]);

  const currentFile = idePeek?.file_path || codeSnapshot?.file_path || null;
  const currentContent = idePeek?.content ?? codeSnapshot?.content ?? '';

  const handleSend = async () => {
    if (!id || !composer.trim() || ctx?.session.status !== 'active') return;
    const generation = generationRef.current;
    const isCurrent = () => generationRef.current === generation;
    const submittedDraft = composer;
    const pending = resolvePendingInstructorMessage(
      pendingMessageRef.current,
      submittedDraft,
    );
    pendingMessageRef.current = pending;
    setSending(true);
    try {
      const msg = await hitlApi.sendMessage(
        id,
        pending.content,
        pending.clientMessageId,
      );
      if (!isCurrent()) return;
      setCtx((prev) =>
        prev ? { ...prev, messages: mergeHitlMessages(prev.messages, msg) } : prev,
      );
      confirmedClientIdsRef.current.add(pending.clientMessageId);
      if (pendingMessageRef.current?.clientMessageId === pending.clientMessageId) {
        pendingMessageRef.current = null;
      }
      setComposer((current) => (current === submittedDraft ? '' : current));
    } catch (err) {
      if (
        isCurrent() &&
        !confirmedClientIdsRef.current.has(pending.clientMessageId)
      ) {
        setError(err instanceof Error ? err.message : 'Failed to send');
      }
    } finally {
      if (isCurrent()) setSending(false);
    }
  };

  const handleAnnotationSave = async () => {
    if (!id || !annNote.trim() || ctx?.session.status !== 'active') return;
    const generation = generationRef.current;
    const isCurrent = () => generationRef.current === generation;
    try {
      const a = await hitlApi.addAnnotation({
        session_id: id,
        line_range_start: annStart,
        line_range_end: annEnd,
        note: annNote,
        file_path: currentFile,
      });
      if (!isCurrent()) return;
      setCtx((prev) =>
        prev ? { ...prev, annotations: mergeHitlAnnotations(prev.annotations, a) } : prev,
      );
      setAnnNote('');
      setAnnotationOpen(false);
    } catch (err) {
      if (isCurrent()) {
        setError(err instanceof Error ? err.message : 'Failed to add annotation');
      }
    }
  };

  if (error && !ctx) {
    return (
      <div style={styles.page}>
        <div
          role="alert"
          style={{ ...styles.card, background: '#FEF2F2', borderColor: '#FCA5A5', color: '#B91C1C' }}
        >
          {error}
        </div>
      </div>
    );
  }
  if (!ctx) return <div style={styles.page}>Loading session…</div>;

  const learnerName =
    ctx.learner.first_name || ctx.learner.last_name
      ? `${ctx.learner.first_name ?? ''} ${ctx.learner.last_name ?? ''}`.trim()
      : ctx.learner.email.split('@')[0];
  const sessionEnded = ctx.session.status !== 'active';

  return (
    <div style={{ ...styles.page, maxWidth: 1400 }}>
      <div style={styles.row}>
        <div style={{ flex: 1 }}>
          <h1 style={styles.h1}>Session with {learnerName}</h1>
          <p style={styles.subtitle}>
            Started {formatTime(ctx.session.taken_at)} · presence: {presence}
          </p>
        </div>
        <button
          style={{ ...styles.button, ...styles.primary }}
          disabled={sessionEnded}
          onClick={() => nav(`/hitl/session/${id}/handback`)}
        >
          {sessionEnded ? 'Handed back' : 'Handback →'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Context column */}
        <div style={styles.colGap}>
          <div style={styles.card}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Learner</div>
            <div>{learnerName}</div>
            <div style={styles.muted}>{ctx.learner.email}</div>
            {ctx.escalation.learner_note && (
              <div style={{ marginTop: 12, padding: 8, background: '#F4F4F5', borderRadius: 6 }}>
                “{ctx.escalation.learner_note}”
              </div>
            )}
          </div>

          {ctx.escalation.source_lecture_id && (
            <div style={styles.card}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Source lecture</div>
              <div style={styles.muted}>{ctx.escalation.source_lecture_id}</div>
            </div>
          )}

          {currentFile && (
            <div style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontWeight: 600 }}>Code snapshot</div>
                <button
                  style={{ ...styles.button, ...styles.secondary, padding: '4px 10px' }}
                  onClick={() => setAnnotationOpen((v) => !v)}
                >
                  {annotationOpen ? 'Close' : 'Annotate'}
                </button>
              </div>
              <div style={styles.muted}>{currentFile}</div>
              <pre
                style={{
                  background: '#0F172A',
                  color: '#E2E8F0',
                  padding: 12,
                  borderRadius: 6,
                  overflowX: 'auto',
                  fontSize: 12,
                  maxHeight: 300,
                  overflowY: 'auto',
                }}
              >
                {currentContent || '(empty)'}
              </pre>
              {annotationOpen && (
                <div style={{ marginTop: 8, padding: 8, background: '#F5E6D8', borderRadius: 6 }}>
                  <div style={{ ...styles.row, marginBottom: 8 }}>
                    <input
                      type="number"
                      min={1}
                      value={annStart}
                      onChange={(e) => setAnnStart(Number(e.target.value))}
                      style={{ ...styles.input, width: 80 }}
                      aria-label="Start line"
                    />
                    <span>–</span>
                    <input
                      type="number"
                      min={annStart}
                      value={annEnd}
                      onChange={(e) => setAnnEnd(Number(e.target.value))}
                      style={{ ...styles.input, width: 80 }}
                      aria-label="End line"
                    />
                  </div>
                  <textarea
                    placeholder="Note (visible to learner)"
                    value={annNote}
                    onChange={(e) => setAnnNote(e.target.value)}
                    maxLength={HITL_ANNOTATION_MAX_LENGTH}
                    style={{ ...styles.input, minHeight: 60, marginBottom: 8 }}
                  />
                  <button
                    style={{ ...styles.button, ...styles.primary }}
                    disabled={sessionEnded || !annNote.trim()}
                    onClick={handleAnnotationSave}
                  >
                    Pin annotation
                  </button>
                </div>
              )}
              {ctx.annotations.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>Annotations</div>
                  {ctx.annotations.map((a) => (
                    <div
                      key={a.id}
                      style={{
                        padding: 8,
                        marginBottom: 6,
                        background: '#F5E6D8',
                        borderLeft: '3px solid #B8600B',
                        borderRadius: 4,
                        fontSize: 13,
                      }}
                    >
                      <div style={styles.muted}>
                        Lines {a.line_range_start}–{a.line_range_end}
                        {a.file_path ? ` · ${a.file_path}` : ''}
                      </div>
                      {a.note}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {ctx.ai_history.length > 0 && (
            <div style={styles.card}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>AI tutor history</div>
              <div style={{ maxHeight: 200, overflowY: 'auto', fontSize: 13 }}>
                {ctx.ai_history.slice(-10).map((m, i) => (
                  <div key={i} style={{ marginBottom: 6 }}>
                    <span style={{ ...styles.violetChip, marginRight: 6 }}>{m.role}</span>
                    {m.content.slice(0, 200)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Conversation column */}
        <div style={styles.card}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Live conversation</div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              maxHeight: 500,
              overflowY: 'auto',
              padding: 8,
            }}
          >
            {ctx.messages.length === 0 && (
              <div style={styles.muted}>No messages yet. Say hello.</div>
            )}
            {ctx.messages.map((m) => (
              <div key={m.id} style={styles.transcriptBubble(m.sender_type)}>
                <div style={{ ...styles.muted, marginBottom: 4 }}>
                  {m.sender_type} · {formatTime(m.sent_at)}
                </div>
                {m.content}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <textarea
              placeholder="Type a message…"
              value={composer}
              onChange={(e) => {
                const next = e.target.value;
                if (
                  pendingMessageRef.current &&
                  pendingMessageRef.current.content !== next.trim()
                ) {
                  pendingMessageRef.current = null;
                }
                setComposer(next);
              }}
              maxLength={HITL_MESSAGE_MAX_LENGTH}
              disabled={sessionEnded}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  void handleSend();
                }
              }}
              style={{ ...styles.input, minHeight: 60, flex: 1 }}
            />
            <button
              disabled={sessionEnded || sending || !composer.trim()}
              style={{
                ...styles.button,
                ...styles.primary,
                opacity: sessionEnded || sending || !composer.trim() ? 0.6 : 1,
              }}
              onClick={handleSend}
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
          <div style={{ ...styles.muted, marginTop: 4 }}>
            Cmd/Ctrl+Enter to send. Learner sees your amber bubbles.
          </div>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            ...styles.card,
            marginTop: 12,
            background: '#FEF2F2',
            borderColor: '#FCA5A5',
            color: '#B91C1C',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

const HitlSession: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) return <div style={styles.page}>Missing session id.</div>;

  // Route identity is the component identity. A param change unmounts the
  // prior state before paint, while the generation fences still protect every
  // deferred operation completing after cleanup.
  return <HitlSessionView key={id} id={id} />;
};

export default HitlSession;
